import type { OverallStatus } from "@statuspage/shared";

interface GlobalStatusBannerProps {
  overallStatus: OverallStatus;
  tenantName: string;
  brandColor: string;
}

const STATUS_CONFIG: Record<
  OverallStatus,
  { bg: string; darkBg: string; text: string; darkText: string; label: string; description: string }
> = {
  operational: {
    bg: "bg-green-600",
    darkBg: "dark:bg-green-700",
    text: "text-white",
    darkText: "dark:text-white",
    label: "All Systems Operational",
    description: "All services are running normally.",
  },
  partial_outage: {
    bg: "bg-yellow-500",
    darkBg: "dark:bg-yellow-600",
    text: "text-white",
    darkText: "dark:text-white",
    label: "Partial Outage",
    description: "Some services are experiencing degraded performance.",
  },
  major_outage: {
    bg: "bg-red-600",
    darkBg: "dark:bg-red-700",
    text: "text-white",
    darkText: "dark:text-white",
    label: "Major Outage",
    description: "One or more services are currently down.",
  },
  under_maintenance: {
    bg: "bg-gray-500",
    darkBg: "dark:bg-gray-700",
    text: "text-white",
    darkText: "dark:text-white",
    label: "Under Maintenance",
    description: "Scheduled maintenance is in progress.",
  },
};

export default function GlobalStatusBanner({
  overallStatus,
  tenantName,
  brandColor,
}: GlobalStatusBannerProps) {
  const config = STATUS_CONFIG[overallStatus];

  return (
    <div
      className={`${config.bg} ${config.darkBg} ${config.text} ${config.darkText} px-4 py-6 sm:px-6 lg:px-8`}
      role="status"
      aria-live="polite"
      aria-label={`${tenantName} status: ${config.label}`}
    >
      <div className="mx-auto max-w-3xl">
        <div className="flex items-start gap-3">
          {brandColor && (
            <div
              className="mt-1 h-10 w-1 flex-shrink-0 rounded-full"
              style={{ backgroundColor: brandColor }}
              aria-hidden="true"
            />
          )}
          <div className="min-w-0">
            <h1 className="text-lg font-semibold sm:text-xl">{tenantName}</h1>
            <p className="mt-1 text-2xl font-bold sm:text-3xl">{config.label}</p>
            <p className="mt-1 text-sm opacity-90">{config.description}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
